import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { DxSelectBoxModule, DxTemplateModule } from 'devextreme-angular';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { UserAvatarComponent } from 'src/app/components/user-avatar/user-avatar.component';
import {
  TaskNotificationRecipient,
  TaskNotificationsService,
  TaskSiteDepartmentRecipient,
} from 'src/app/services/task-notifications.service';

export interface TaskNotificationRow {
  userEmail: string | null;
  /** #4994: a whole department is notified (every member); displayName carries its name. */
  departmentId: number | null;
  displayName: string;
  /** The department the person is notified through, shown under their name. */
  roleName: string | null;
  /** For a site task: the projects on the site this person answers for the department on. */
  projectNames: string | null;
  /** A department the project has nobody behind: displayName carries the department name. */
  unassigned: boolean;
  removable: boolean;
}

/**
 * Everyone this task notifies when it is completed (#4625). The responsible person comes straight
 * from the task row — the page already resolved the department and the direct assignee — so only
 * the additional recipients are fetched, and only those are stored. Whoever the task's
 * configuration names (#4624) comes back among them but cannot be removed here. Edits are staged
 * and committed by the popup's Confirm button.
 */
@Component({
  selector: 'app-task-notifications-panel',
  imports: [CommonModule, DxSelectBoxModule, DxTemplateModule, SvgIconComponent, UserAvatarComponent],
  templateUrl: './task-notifications-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./task-notifications-panel.component.css'],
})
export class TaskNotificationsPanelComponent implements OnChanges {
  @Input() projectTaskId: number | null = null;

  /** The task's resolved responsible: its direct assignee, or the person behind its department. */
  readonly assignedUserEmail = input<string | null>(null);

  /** The person behind the task's department, null when the project has nobody for it. */
  readonly departmentUserEmail = input<string | null>(null);

  readonly departmentName = input<string | null>(null);

  /** #4994: the department holding the task's role, instead of departmentUserEmail. */
  readonly departmentHolderId = input<number | null>(null);
  readonly departmentHolderName = input<string | null>(null);

  /** #4994: the department the task itself is assigned to (or, folded in, its role holder). */
  readonly assignedDepartmentId = input<number | null>(null);
  readonly assignedDepartmentName = input<string | null>(null);

  /** The people who can be picked, as the task page already loaded them. */
  readonly users = input<any[]>([]);

  readonly additional = signal<TaskNotificationRecipient[]>([]);
  readonly siteDepartment = signal<TaskSiteDepartmentRecipient[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');

  readonly hint = $localize`:@@taskNotificationsHint:These recipients are configured in the task settings and cannot be changed here.`;
  readonly placeholder = $localize`:@@taskNotificationsPlaceholder:Add another person...`;
  readonly unassignedLabel = $localize`:@@taskNotificationsUnassignedRole:No person assigned to this role yet`;

  readonly rows = computed<TaskNotificationRow[]>(() => {
    const rows: TaskNotificationRow[] = [];
    const department = this.departmentName();
    const departmentUser = this.departmentUserEmail();
    const responsible = this.assignedUserEmail();
    const siteDepartment = this.siteDepartment();
    const holderDepartmentId = this.departmentHolderId();
    const assignedDepartmentId = this.assignedDepartmentId();

    if (siteDepartment.length > 0) {
      // One card per person; someone behind the department on several projects lists them all.
      const byEmail = new Map<string, TaskSiteDepartmentRecipient[]>();
      for (const entry of siteDepartment) {
        // #4994: a department-held role on a project has no email; key it by the department.
        const key = entry.userEmail?.toLowerCase() ?? `department:${entry.departmentId}`;
        byEmail.set(key, [...(byEmail.get(key) ?? []), entry]);
      }

      for (const entries of byEmail.values()) {
        rows.push({
          userEmail: entries[0].userEmail ?? null,
          departmentId: entries[0].departmentId ?? null,
          displayName: entries[0].displayName || entries[0].departmentName || this.displayNameOf(entries[0].userEmail),
          roleName: department || null,
          projectNames: entries.map(entry => entry.projectName).join(', '),
          unassigned: false,
          removable: false,
        });
      }
    } else if (departmentUser) {
      rows.push({
        userEmail: departmentUser,
        departmentId: null,
        displayName: this.displayNameOf(departmentUser),
        roleName: department || null,
        projectNames: null,
        unassigned: false,
        removable: false,
      });
    } else if (holderDepartmentId != null) {
      rows.push({
        userEmail: null,
        departmentId: holderDepartmentId,
        displayName: this.departmentHolderName() || '',
        roleName: department || null,
        projectNames: null,
        unassigned: false,
        removable: false,
      });
    } else if (department) {
      rows.push({
        userEmail: null,
        departmentId: null,
        displayName: department,
        roleName: null,
        projectNames: null,
        unassigned: true,
        removable: false,
      });
    }

    // assignedUserEmail already falls back to the department, so it only names someone new when it
    // differs - a direct assignee, who stands for themselves rather than for the department.
    if (
      responsible &&
      responsible.toLowerCase() !== departmentUser?.toLowerCase() &&
      !rows.some(row => row.userEmail?.toLowerCase() === responsible.toLowerCase())
    ) {
      rows.push({
        userEmail: responsible,
        departmentId: null,
        displayName: this.displayNameOf(responsible),
        roleName: null,
        projectNames: null,
        unassigned: false,
        removable: false,
      });
    }

    // #4994: a task assigned to a whole department; equal to the role holder when only folded in.
    if (assignedDepartmentId != null && !rows.some(row => row.departmentId === assignedDepartmentId)) {
      rows.push({
        userEmail: null,
        departmentId: assignedDepartmentId,
        displayName: this.assignedDepartmentName() || '',
        roleName: null,
        projectNames: null,
        unassigned: false,
        removable: false,
      });
    }

    for (const recipient of this.additional()) {
      if (rows.some(row => row.userEmail?.toLowerCase() === recipient.userEmail?.toLowerCase())) continue;

      rows.push({
        userEmail: recipient.userEmail,
        departmentId: null,
        displayName: recipient.displayName,
        roleName: null,
        projectNames: null,
        unassigned: false,
        removable: recipient.removable,
      });
    }

    return rows;
  });

  // Someone already on the list cannot be added a second time.
  readonly selectableUsers = computed(() => {
    const listed = new Set(
      this.rows()
        .map(row => row.userEmail?.toLowerCase())
        .filter(Boolean),
    );

    return (this.users() ?? []).filter(user => user.emailAddress && !listed.has(user.emailAddress.toLowerCase()));
  });

  constructor(private notifications: TaskNotificationsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectTaskId'] && this.projectTaskId) void this.load();
  }

  async load(): Promise<void> {
    if (!this.projectTaskId) return;

    this.loading.set(true);
    this.error.set('');

    try {
      this.apply(await this.notifications.getRecipients(this.projectTaskId));
    } catch (error: any) {
      this.error.set(error?.message || $localize`:@@taskNotificationsLoadFailed:The recipients could not be loaded.`);
    } finally {
      this.loading.set(false);
    }
  }

  onPersonSelected(email: string | null, picker: any): void {
    const user = (this.users() ?? []).find(candidate => candidate.emailAddress === email);

    if (!user) return;

    this.additional.update(current => [
      ...current,
      {
        userEmail: user.emailAddress,
        displayName: user.displayName || user.emailAddress,
        removable: true,
      },
    ]);

    // The picker is an "add" control, not a selection: it goes back to its placeholder so the next
    // person can be picked straight away.
    picker?.instance?.reset();
  }

  remove(row: TaskNotificationRow): void {
    this.additional.update(current =>
      current.filter(recipient => recipient.userEmail.toLowerCase() !== row.userEmail?.toLowerCase()),
    );
  }

  /** Commits the staged list. Returns false when the save failed and the popup should stay open. */
  async save(): Promise<boolean> {
    if (!this.projectTaskId) return true;

    this.saving.set(true);
    this.error.set('');

    try {
      const emails = this.additional()
        .filter(recipient => recipient.removable && recipient.userEmail)
        .map(recipient => recipient.userEmail);

      this.apply(await this.notifications.saveRecipients(this.projectTaskId, emails));

      return true;
    } catch (error: any) {
      this.error.set(error?.message || $localize`:@@taskNotificationsSaveFailed:The recipients could not be saved.`);

      return false;
    } finally {
      this.saving.set(false);
    }
  }

  private apply(result: { recipients: TaskNotificationRecipient[]; siteDepartmentRecipients: TaskSiteDepartmentRecipient[] }): void {
    this.additional.set(result.recipients);
    this.siteDepartment.set(result.siteDepartmentRecipients);
  }

  initials(row: TaskNotificationRow): string {
    if (row.unassigned) return '';

    return (row.displayName || '')
      .split(' ')
      .filter(part => part.length > 0)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  }

  private displayNameOf(email: string): string {
    return (this.users() ?? []).find(user => user.emailAddress === email)?.displayName || email;
  }
}
